<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import { useStageDefaultEmissions } from "@/dataProviders/StageDefaultEmissions";
import { WizardProjectType } from "@/features/BuildingProject";
import { Country_Enum, useGetStageDefaultEmissionLazyQuery, useGetProjectStandardValueStagesQuery } from "@/graphql";
import { computed, onMounted, ref, watch } from "vue";
import { Info, Table2 } from "@lucide/vue";
import Switch from "@/components/Reusables/Switches/Switch.vue";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import StandardValuesModal from "@/features/BuildingProject/components/WizardFlow/StandardValues/StandardValuesModal.vue";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";
import StandardValuesTable from "./StandardValuesTable.vue";
import StepIntro from "../StepIntro/StepIntro.vue";
import { useIndicators } from "@/dataProviders/Indicators";
import { useConfiguration } from "@/dataProviders/Configuration";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";
import { standardValueStageDisplayNameFor } from "@/features/BuildingProject/utils/standardValueStageCopy";

const store = useStageDefaultEmissions();
const { mapData, setEnabledStages, setBuildingTypeFilter, getBuildingTypeFilteredEmissions } = store;

// Rows restricted via `stageDefaultEmissionBuildingTypes` are hidden (and excluded from calculations)
// unless the project's building type is one of theirs; unrestricted rows always apply.
const visibleEmissions = computed(() => getBuildingTypeFilteredEmissions());

const onShowStages = ref(false);
const onShowMapping = ref(false);

const { loading, load } = useGetStageDefaultEmissionLazyQuery();
const indicators = useIndicators();

const mappingStages = ref([
  {
    label: "Raw material acquisition (A1)",
    value: "+25%",
  },
  {
    label: "Transport to Manufacturing Site (A2)",
    value: "+25%",
  },
  {
    label: "Production (A3)",
    value: "+25%",
  },
]);

const stagesDataByCountry: Partial<Record<Country_Enum, { title: string; text: string[] }>> = {
  [Country_Enum.ICELAND]: {
    title: "Notice Regarding Missing Phases in EPD",
    text: [
      // "Calculation Prerequisites" was a step of its own until it folded into Project; the life-cycle
      // stages this refers to are set on the Project section of Settings now.
      "When a mapping does not include the necessary stages defined under Project in the project's Settings, the missing stages will be replaced with the following standard values to ensure the accuracy of the annual emissions calculations.",
      "These adjustments ensure the material mapping aligns with the project's calculation requirements and provides a complete and reliable emissions assessment.",
      "Note that if the average value for C1-C4 is used, it must be ensured that the emissions of organic carbon bound in e.g. timber products are not ignored (if the so-called -1/+1 approach is used for organic carbon in the data used).",
    ],
  },
  [Country_Enum.DENMARK]: {
    title: "Notice Regarding Terminal Processes",
    text: [
      "If transport is calculated based on Table 10, an additional 0.02 kg CO₂ equivalents per m² of building per year is added to account for the transport of surplus materials, packaging, and similar.",
      "Special note on terminal processes: All terminal processes related to transport (in module A4) are, in total, calculated as 0.02 kg CO₂ equivalents per m² of building per year.",
    ],
  },
};

const modalData = computed(() => {
  const country = calculationType.value?.country;
  const stagesData = country ? stagesDataByCountry[country] : undefined;

  const mappingData = {
    title: "Notice Regarding non-EPD mapping",
    text: [
      "When mapping with a material that is classified as a <em>Generic Material</em> we apply the following percentage increases to the annual emissions calculations.",
      "These adjustments ensure that the emissions calculations account for the lack of validated EPD data and align with project standards.",
    ],
  };
  return onShowStages.value ? stagesData : mappingData;
});

// Countries without an entry in `stagesDataByCountry` (e.g. Finland) hide the (i) trigger next to the
// heading, so it can't open an empty modal.
const stagesNotice = computed(() => {
  const country = calculationType.value?.country;
  return country ? stagesDataByCountry[country] : undefined;
});

const isActive = ref(false);
const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /** Suppresses this component's own StepIntro for the merged Allowances step, which renders one h1 for both halves; additive and defaulted so the edit wizard's separate step is untouched. */
    hideIntro?: boolean;
  }>(),
  { hideIntro: false }
);

const { project, projectId, lens } = useWizardProject(() => props.project);

const useDefaultEmissions = lens(
  (current) => current.emissionCalculationAssumptions.calculationType.useDefaultEmissions,
  (current, value) => (current.emissionCalculationAssumptions.calculationType.useDefaultEmissions = value)
);

const useDefaultEmissionsValue = computed(() => {
  return project.value?.emissionCalculationAssumptions?.calculationType?.useDefaultEmissions;
});

// Gated by the country config's `useStageDefaultEmissionByStage` flag. Held locally and mirrored into
// the `useStageDefaultEmissions` provider; persisted to Hasura only on Save, so create and edit behave identically.

// Keeps `visibleEmissions` (and every calculation path on the provider) in sync with the project's
// building type. A missing / placeholder selection (`null` / `-1`) disables the filtering.
watch(
  () => project.value.buildingTypeEnum,
  (newBuildingType) => {
    if (newBuildingType === null || newBuildingType === undefined) {
      setBuildingTypeFilter(null);
      return;
    }
    const buildingTypeId = Number(newBuildingType);
    setBuildingTypeFilter(Number.isFinite(buildingTypeId) && buildingTypeId > -1 ? buildingTypeId : null);
  },
  { immediate: true }
);

const enabledStages = ref<Record<string, boolean>>({});

// The shared Switch only reads its `value` prop on mount, so a switch this component changes on the
// reader's behalf must be re-created via these keys — bumped only when the reader didn't click it.
const stageSwitchResyncKey = ref(0);
const masterSwitchResyncKey = ref(0);

// `asSaved` marks the map as server state rather than an edit — only the seed and the reset below pass
// it, which is what lights the settings rail's "edited" marker for this section.
const syncProviderEnabledStages = (options?: { asSaved?: boolean }) => {
  setEnabledStages({ ...enabledStages.value }, options);
};

const calculationTypes = useCalculationTypes();

const calculationType = computed(() => {
  const calculationTypeEnum = project.value.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;

  if (calculationTypeEnum === null) {
    return null;
  }

  return calculationTypes.getSelectedCalculationType(calculationTypeEnum);
});

const configuration = useConfiguration();

// Driven by `useStageDefaultEmissionByStage` on the country config. `getConfiguration()` is read here
// so the computed re-evaluates once the config provider loads.
const supportsPerStageToggle = computed(() => {
  configuration.getConfiguration();
  return configuration.useStageDefaultEmissionByStage(calculationType.value?.id ?? null);
});

// Only runs for projects whose config enables per-stage toggles — other countries don't expose the
// per-row toggles.
const enabledStagesQueryVars = computed(() => ({ projectId: projectId.value ?? "" }));
const enabledStagesQueryOptions = computed(() => ({
  enabled: Boolean(projectId.value) && supportsPerStageToggle.value,
  fetchPolicy: "no-cache" as const,
}));
const { result: enabledStagesResult, loading: loadingEnabledStages } = useGetProjectStandardValueStagesQuery(
  enabledStagesQueryVars,
  enabledStagesQueryOptions
);

const tableEnabledMap = computed<Record<string, boolean> | undefined>(() => {
  if (!supportsPerStageToggle.value) return undefined;
  const map: Record<string, boolean> = {};
  for (const emission of visibleEmissions.value) {
    const key = emission.id;
    map[key] = enabledStages.value[key] ?? false;
  }
  return map;
});

const tableItems = computed(() => {
  return visibleEmissions.value.map((emission) => ({
    id: emission.id,
    label: standardValueStageDisplayNameFor(emission.label),
    value: emission.value,
  }));
});

// Writes the master "Use standard values" switch only — `updateUseDefaultEmissions` below is the
// select-all/deselect-all path; this just keeps the master switch agreeing with a per-row change.
const setUseDefaultEmissions = (value: boolean) => {
  if (useDefaultEmissions.value === value) return;
  useDefaultEmissions.value = value;
  isActive.value = value;
  masterSwitchResyncKey.value += 1;
};

const onToggleStage = (id: string, enabled: boolean) => {
  // The shared Switch re-emits its value once on mount, so every `stageSwitchResyncKey` bump fires this
  // handler with the unchanged value — ignore no-op emits or a server-seeded row reads as a reader's edit.
  if (enabled === (enabledStages.value[id] ?? false)) return;

  // Per-row toggles are independent of each other — only the master toggle drives select-all /
  // deselect-all.
  const next = { ...enabledStages.value, [id]: enabled };
  enabledStages.value = next;
  syncProviderEnabledStages();

  // A stage row is a way in of its own: switching one on switches the master toggle on with it, so
  // wanting standard values for Terminal processes (A4) is one action instead of two.
  if (enabled) {
    setUseDefaultEmissions(true);
    return;
  }

  // Switching the last enabled stage off switches the master toggle back off too — "Use standard values"
  // on with no stage enabled would misreport as complete to the settings rail's `hasData` check.
  const anyStageEnabled = visibleEmissions.value.some((emission) => next[emission.id]);
  if (!anyStageEnabled) setUseDefaultEmissions(false);
};

const defaultEmissionsTitle = computed(() => {
  if (calculationType.value?.country === Country_Enum.DENMARK) {
    return "Standard values for terminal processes";
  }

  if (calculationType.value?.country === Country_Enum.ICELAND) {
    return "Standard values for missing stages in EPD";
  }

  if (calculationType.value?.country === Country_Enum.FINLAND) {
    return "Standard values";
  }

  return undefined;
});

const showNonEpdMappingStandardValues = computed(() => {
  return calculationType.value?.country === Country_Enum.ICELAND;
});

const standardValueIndicatorUnit = computed(() => {
  // get indicator and denominator unit from first item in visibleEmissions
  const firstItem = Array.isArray(visibleEmissions.value) && visibleEmissions.value.length > 0 ? visibleEmissions.value[0] : null;

  if (!firstItem) {
    return "";
  }

  return indicators.getIndicatorUnit(firstItem.indicatorType, firstItem.denominatorUnit || undefined);
});

onMounted(() => {
  isActive.value = project.value.emissionCalculationAssumptions.calculationType.useDefaultEmissions;
});

watch(
  calculationType,
  async (newValue) => {
    if (newValue) {
      const data = await load(null, {
        country: newValue.country,
      });

      if (typeof data === "object" && data?.stageDefaultEmission) {
        mapData(data);
      }
    }
  },
  { immediate: true }
);

watch(
  useDefaultEmissionsValue,
  (newVal) => {
    isActive.value = newVal;
  },
  { immediate: true }
);

// Row presence in the query result = enabled; rows for stages that no longer exist for the country, or
// are hidden for the project's building type, are ignored.
watch(
  [visibleEmissions, supportsPerStageToggle, enabledStagesResult],
  ([newEmissions, perStageToggle, queryResult]) => {
    if (!perStageToggle) {
      enabledStages.value = {};
      setEnabledStages({}, { asSaved: true });
      return;
    }
    if (newEmissions.length === 0) return;
    const enabledFromServer = new Set<string>();
    const rows = queryResult?.projectStandardValueStage ?? [];
    for (const row of rows) {
      enabledFromServer.add(row.stageId);
    }
    const next: Record<string, boolean> = {};
    for (const emission of newEmissions) {
      next[emission.id] = enabledFromServer.has(emission.id);
    }
    enabledStages.value = next;
    // Built from the `projectStandardValueStage` rows a few lines up, so this IS the server's state.
    syncProviderEnabledStages({ asSaved: true });
    stageSwitchResyncKey.value += 1;
  },
  { immediate: true }
);

const updateUseDefaultEmissions = (val: boolean) => {
  // The shared Switch re-emits its value once on mount, so this handler fires on every step-show with
  // the unchanged value — ignore no-op emits or the select-all below clobbers rows just loaded from Hasura.
  if (val === useDefaultEmissions.value) return;

  useDefaultEmissions.value = val;
  isActive.value = val;

  // `useDefaultEmissions` is persisted as part of the project save (it lives on calculationType), so
  // this only mutates the local project; the per-row select-all/deselect-all below persists on Save too.
  if (!supportsPerStageToggle.value || visibleEmissions.value.length === 0) return;

  const next: Record<string, boolean> = {};
  for (const emission of visibleEmissions.value) {
    next[emission.id] = val;
  }
  enabledStages.value = next;
  syncProviderEnabledStages();
  // The rows moved because of a click on the master switch, not on themselves.
  stageSwitchResyncKey.value += 1;
};
</script>

<template>
  <LoadingWrapper :loading="loading || loadingEnabledStages">
    <StandardValuesModal
      v-if="calculationType && (onShowStages || onShowMapping)"
      :data="modalData"
      @toggle-modal="
        () => {
          onShowStages = false;
          onShowMapping = false;
        }
      " />
    <div class="flex w-full flex-col">
      <StepIntro
        v-if="!hideIntro"
        class="mb-4"
        heading="Which standard values apply?"
        hint="Percentage increases applied where a material has no validated EPD. Switch on the stages the project should use them for." />

      <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
                <Table2
                  class="icon-sm text-app-blue-600"
                  :stroke-width="1.5"
                  aria-hidden="true" />
              </span>
              <CardHeading class="t-sh4 text-type-default">Standard values</CardHeading>
            </div>
            <Switch
              :key="`use-standard-values-${masterSwitchResyncKey}`"
              color="primary"
              :value="isActive"
              label="Use standard values"
              @update:value="updateUseDefaultEmissions" />
          </div>

          <!-- Renders only once defaultEmissionsTitle resolves — before then this said nothing to a screen
               reader either, which fails the story's a11y gate. -->
          <div
            v-if="defaultEmissionsTitle"
            class="flex items-center gap-1">
            <p class="t-caption text-type-muted">{{ defaultEmissionsTitle }}</p>
            <button
              v-if="stagesNotice"
              type="button"
              aria-label="About the standard-value stages"
              class="focus-ring interactive-active duration-fast ease-standard p-px-2 rounded-4 transition-colors hover:bg-slate-100"
              @click="onShowStages = true">
              <Info
                class="icon-xs text-slate-500"
                :stroke-width="1.5" />
            </button>
          </div>
        </div>
        <StandardValuesTable
          :headers="{ label: 'Stage', value: standardValueIndicatorUnit }"
          :items="tableItems"
          :enabled-map="tableEnabledMap"
          :resync-key="stageSwitchResyncKey"
          @toggle-item="onToggleStage" />

        <template v-if="showNonEpdMappingStandardValues">
          <div class="flex items-center gap-1">
            <p class="t-caption text-type-muted">Standard values for mapping of non-EPD</p>
            <button
              type="button"
              aria-label="About the non-EPD mapping standard values"
              class="focus-ring interactive-active duration-fast ease-standard p-px-2 rounded-4 transition-colors hover:bg-slate-100"
              @click="onShowMapping = true">
              <Info
                class="icon-xs text-slate-500"
                :stroke-width="1.5" />
            </button>
          </div>

          <StandardValuesTable
            :headers="{ label: 'Stage', value: '%-addition' }"
            :items="mappingStages" />
        </template>
      </div>
    </div>
  </LoadingWrapper>
</template>
